/**
 * Toast - Animated slide-in notification component.
 * Renders a stack of toasts at the bottom-right corner with auto-dismiss.
 */
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { CheckCircle, Info, AlertTriangle, XCircle, X } from 'lucide-react';

export type ToastType = 'success' | 'info' | 'warning' | 'error';

export interface ToastItem {
  id: string;
  message: string;
  type: ToastType;
}

const TOAST_DURATION = 3500; // ms

const TYPE_CONFIG: Record<ToastType, { icon: React.FC<{ className?: string }>; borderColor: string; iconColor: string; bgGlow: string }> = {
  success: {
    icon: CheckCircle,
    borderColor: '#22c55e',
    iconColor: '#4ade80',
    bgGlow: 'rgba(34,197,94,0.08)',
  },
  info: {
    icon: Info,
    borderColor: '#3b82f6',
    iconColor: '#60a5fa',
    bgGlow: 'rgba(59,130,246,0.08)',
  },
  warning: {
    icon: AlertTriangle,
    borderColor: '#f59e0b',
    iconColor: '#fbbf24',
    bgGlow: 'rgba(245,158,11,0.08)',
  },
  error: {
    icon: XCircle,
    borderColor: '#ef4444',
    iconColor: '#f87171',
    bgGlow: 'rgba(239,68,68,0.08)',
  },
};

const SingleToast: React.FC<{ toast: ToastItem; onDismiss: (id: string) => void }> = ({ toast, onDismiss }) => {
  const [progress, setProgress] = useState(100);
  const config = TYPE_CONFIG[toast.type];
  const IconComponent = config.icon;

  useEffect(() => {
    const start = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - start;
      const remaining = Math.max(0, 100 - (elapsed / TOAST_DURATION) * 100);
      setProgress(remaining);
      if (remaining <= 0) {
        clearInterval(interval);
        onDismiss(toast.id);
      }
    }, 30);
    return () => clearInterval(interval);
  }, [toast.id, onDismiss]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 80, scale: 0.85 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 80, scale: 0.85 }}
      transition={{ type: 'spring', stiffness: 500, damping: 35 }}
      onClick={() => onDismiss(toast.id)}
      style={{
        borderLeft: `3px solid ${config.borderColor}`,
        background: `linear-gradient(135deg, ${config.bgGlow}, rgba(15,23,42,0.92) 60%)`,
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
      }}
      className="relative overflow-hidden rounded-lg shadow-2xl cursor-pointer min-w-[300px] max-w-[420px] border border-white/10"
    >
      <div className="flex items-start gap-3 px-4 py-3">
        <IconComponent className="w-5 h-5 shrink-0 mt-0.5" style={{ color: config.iconColor }} />
        <p className="text-sm text-slate-100 leading-relaxed flex-1 select-text">{toast.message}</p>
        <button
          onClick={(e) => { e.stopPropagation(); onDismiss(toast.id); }}
          className="p-0.5 text-slate-400 hover:text-white transition-colors shrink-0 cursor-pointer"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      {/* Progress bar */}
      <div className="h-[2px] w-full bg-black/20">
        <motion.div
          className="h-full"
          style={{
            background: config.borderColor,
            width: `${progress}%`,
          }}
          transition={{ duration: 0 }}
        />
      </div>
    </motion.div>
  );
};

export const Toast: React.FC<{ toasts: ToastItem[]; onDismiss: (id: string) => void }> = ({ toasts, onDismiss }) => {
  return (
    <div
      style={{ zIndex: 99999 }}
      className="fixed bottom-4 right-4 flex flex-col-reverse gap-2 pointer-events-auto"
    >
      <AnimatePresence mode="popLayout">
        {toasts.map((t) => (
          <SingleToast key={t.id} toast={t} onDismiss={onDismiss} />
        ))}
      </AnimatePresence>
    </div>
  );
};
